import { useMutation } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import Toaster from 'components/Toaster';
import {
  AttachmentSection,
  AttachmentHeader,
  AddImageButton,
  AttachmentInfo,
  AttachmentStatus,
  FileCountBadge,
  ExpandIcon,
  AttachmentContent,
  DragOverlay,
  FileGrid,
  FileItem,
  FileOverlay,
  ViewFileButton,
  RemoveFileButton,
  AddMoreArea,
  ImagePreviewOverlay,
  ImagePreviewContainer,
  ImagePreviewImage,
  ImagePreviewClose,
  ImagePreviewFileName,
  ImagePreviewPDF,
  ImagePreviewPDFLink,
} from './StyledComponents';

// GraphQL mutation for file upload
const SINGLE_UPLOAD = gql`
  mutation SingleUpload($file: Upload!) {
    singleUpload(file: $file) {
      id
      url
    }
  }
`;

const AttachmentUpload = ({
  attachments,
  onAttachmentsChange,
  maxFiles = 3,
  maxFileSize = 10485760, // 10MB (files stored in S3, only links sent in SMS)
  channel = 'SMS',
  disabled = false,
  onError = null, // Callback for error messages
  defaultExpanded = false, // Whether to start expanded
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [uploading, setUploading] = useState(false);
  const [toasterOpen, setToasterOpen] = useState(false);
  const [toasterConfig, setToasterConfig] = useState({});
  const [previewFile, setPreviewFile] = useState(null);
  const [activeFileId, setActiveFileId] = useState(null);
  const fileInputRef = useRef(null);
  const fileGridRef = useRef(null);

  // Initialize the upload mutation
  const [singleUpload] = useMutation(SINGLE_UPLOAD);

  // Show error toaster
  const showError = (title, message) => {
    if (onError) {
      onError({ title, message, type: 'error' });
    } else {
      setToasterConfig({ title, message, type: 'error' });
      setToasterOpen(true);
    }
  };

  // Handle ESC key to close preview modal
  useEffect(() => {
    if (!previewFile) return;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setPreviewFile(null);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [previewFile]);

  // Handle clicking outside to close active file overlay (for mobile)
  useEffect(() => {
    if (!activeFileId) return;

    const handleClickOutside = (event) => {
      if (fileGridRef.current && !fileGridRef.current.contains(event.target)) {
        setActiveFileId(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeFileId]);

  // Core file processing logic shared by click and drag-and-drop
  const processFiles = useCallback(
    async (files) => {
      setUploading(true);

      try {
        // When maxFiles is 1, replace existing attachments instead of appending
        const newAttachments = maxFiles === 1 ? [] : [...attachments];

        for (const file of files) {
          if (newAttachments.length >= maxFiles) {
            showError(
              'File Limit Exceeded',
              `Maximum ${maxFiles} files allowed`
            );
            break;
          }

          // Allow images, SVGs, and PDFs
          const isImage = file.type.startsWith('image/');
          const isPDF = file.type === 'application/pdf';
          const isSVG = file.type === 'image/svg+xml';

          if (!isImage && !isPDF && !isSVG) {
            showError(
              'Invalid File Type',
              'Please select only image files (JPG, PNG, GIF, SVG) or PDF files'
            );
            continue;
          }

          if (file.size > maxFileSize) {
            showError(
              'File Too Large',
              `${file.name} is too large. Maximum size is ${formatFileSize(
                maxFileSize
              )}`
            );
            continue;
          }

          try {
            // Upload file to server
            const { data } = await singleUpload({
              variables: { file },
            });

            if (data?.singleUpload) {
              // Properly encode the URL to handle spaces and special characters
              // The URL might have spaces in the filename part
              let url = data.singleUpload.url;

              // Check if URL contains spaces and encode them
              if (url && url.includes(' ')) {
                // Split URL into base and filename parts
                const lastSlashIndex = url.lastIndexOf('/');
                if (lastSlashIndex !== -1) {
                  const baseUrl = url.substring(0, lastSlashIndex + 1);
                  const filename = url.substring(lastSlashIndex + 1);
                  // Encode the filename part
                  url = baseUrl + encodeURIComponent(filename);
                }
              }

              // Add the uploaded file with server URL and ID
              newAttachments.push({
                id: data.singleUpload.id, // Server-generated ID
                name: file.name,
                size: file.size,
                sizeFormatted: formatFileSize(file.size),
                url: url, // Properly encoded server URL
                type: file.type,
                isImage: isImage,
                isPDF: isPDF,
                isSVG: isSVG,
              });
            }
          } catch (uploadError) {
            showError(
              'Upload Failed',
              `Failed to upload ${file.name}. Please try again.`
            );
          }
        }

        onAttachmentsChange(newAttachments);
        if (newAttachments.length > 0) {
          setIsExpanded(true);
        }
      } finally {
        setUploading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [attachments, maxFiles, maxFileSize, onAttachmentsChange, singleUpload]
  );

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);

    // Reset the input immediately after getting files
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    await processFiles(files);
  };

  // Drag-and-drop support
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: processFiles,
    accept: 'image/jpeg, image/png, image/gif, image/svg+xml, application/pdf',
    noClick: true, // We handle click via the existing button
    noKeyboard: true,
    disabled: disabled || channel === 'Push',
  });

  const removeFile = (index) => {
    const newAttachments = attachments.filter((_, i) => i !== index);
    onAttachmentsChange(newAttachments);
    if (newAttachments.length === 0) {
      setIsExpanded(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const triggerUpload = (e) => {
    e.stopPropagation();
    if (!disabled && (maxFiles === 1 || attachments.length < maxFiles)) {
      fileInputRef.current.click();
    }
  };

  const toggleExpanded = () => {
    if (attachments.length > 0) {
      setIsExpanded(!isExpanded);
    }
  };

  const handleFileClick = (file, e) => {
    e.stopPropagation();
    // Toggle active state (show/hide overlay)
    setActiveFileId(activeFileId === file.id ? null : file.id);
  };

  const openPreview = (file, e) => {
    e.stopPropagation();
    setPreviewFile(file);
    setActiveFileId(null); // Close overlay when opening preview
  };

  const closePreview = (e) => {
    if (e) e.stopPropagation();
    setPreviewFile(null);
  };

  const hasAttachments = attachments.length > 0;

  // Disabled for Push notifications
  if (channel === 'Push') {
    return (
      <AttachmentSection disabled>
        <AttachmentHeader>
          <AddImageButton type="button" disabled>
            <svg
              width="14"
              height="14"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add File
          </AddImageButton>
          <AttachmentInfo>
            Attachments not available for Push notifications
          </AttachmentInfo>
        </AttachmentHeader>
      </AttachmentSection>
    );
  }

  return (
    <>
      <AttachmentSection
        {...getRootProps()}
        hasFiles={hasAttachments}
        expanded={isExpanded}
        disabled={disabled}
        isDragActive={isDragActive}
      >
        {isDragActive && (
          <DragOverlay>
            <svg
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
              />
            </svg>
            Drop files here
          </DragOverlay>
        )}
        <AttachmentHeader onClick={toggleExpanded}>
          <AddImageButton
            type="button"
            onClick={triggerUpload}
            disabled={disabled || uploading}
          >
            {uploading ? (
              <>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  style={{
                    animation: 'spin 1s linear infinite',
                  }}
                >
                  <circle
                    cx="8"
                    cy="8"
                    r="6"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                    strokeDasharray="30 10"
                  />
                </svg>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                Uploading...
              </>
            ) : (
              <>
                <svg
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add File
              </>
            )}
          </AddImageButton>

          {!hasAttachments ? (
            <AttachmentInfo>
              JPG, PNG, GIF or PDF • Max {formatFileSize(maxFileSize)} per file
              • Up to {maxFiles} files
            </AttachmentInfo>
          ) : (
            <AttachmentStatus>
              <FileCountBadge>
                <svg
                  width="12"
                  height="12"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                {attachments.length}{' '}
                {attachments.length === 1 ? 'file' : 'files'}
              </FileCountBadge>
            </AttachmentStatus>
          )}

          {hasAttachments && (
            <ExpandIcon expanded={isExpanded}>
              <svg
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </ExpandIcon>
          )}
        </AttachmentHeader>

        {isExpanded && (
          <AttachmentContent>
            <FileGrid ref={fileGridRef}>
              {attachments.map((file, index) => (
                <FileItem
                  key={file.id}
                  className="file-item"
                  onClick={(e) => handleFileClick(file, e)}
                >
                  {file.isPDF ? (
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(250, 70, 22, 0.1)',
                        borderRadius: '8px',
                      }}
                    >
                      <svg
                        width="32"
                        height="32"
                        fill="#FA4616"
                        viewBox="0 0 16 16"
                        style={{ marginBottom: '8px' }}
                      >
                        <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2zM9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5v2z" />
                        <path d="M4.603 14.087a.81.81 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.68 7.68 0 0 1 1.482-.645 19.697 19.697 0 0 0 1.062-2.227 7.269 7.269 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 0 1 .477.365c.088.164.12.356.127.538.007.188-.012.396-.047.614-.084.51-.27 1.134-.52 1.794a10.954 10.954 0 0 0 .98 1.686 5.753 5.753 0 0 1 1.334.05c.364.066.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 0 1-.354.416.856.856 0 0 1-.51.138c-.331-.014-.654-.196-.933-.417a5.712 5.712 0 0 1-.911-.95 11.651 11.651 0 0 0-1.997.406 11.307 11.307 0 0 1-1.02 1.51c-.292.35-.609.656-.927.787a.793.793 0 0 1-.58.029zm1.379-1.901c-.166.076-.32.156-.459.238-.328.194-.541.383-.647.547-.094.145-.096.25-.04.361.01.022.02.036.026.044a.266.266 0 0 0 .035-.012c.137-.056.355-.235.635-.572a8.18 8.18 0 0 0 .45-.606zm1.64-1.33a12.71 12.71 0 0 1 1.01-.193 11.744 11.744 0 0 1-.51-.858 20.801 20.801 0 0 1-.5 1.05zm2.446.45c.15.163.296.3.435.41.24.19.407.253.498.256a.107.107 0 0 0 .07-.015.307.307 0 0 0 .094-.125.436.436 0 0 0 .059-.2.095.095 0 0 0-.026-.063c-.052-.062-.2-.152-.518-.209a3.876 3.876 0 0 0-.612-.053zM8.078 7.8a6.7 6.7 0 0 0 .2-.828c.031-.188.043-.343.038-.465a.613.613 0 0 0-.032-.198.517.517 0 0 0-.145.04c-.087.035-.158.106-.196.283-.04.192-.03.469.046.822.024.111.054.227.09.346z" />
                      </svg>
                      <span
                        style={{
                          fontSize: '10px',
                          color: '#FA4616',
                          textAlign: 'center',
                          padding: '0 4px',
                          wordBreak: 'break-all',
                        }}
                      >
                        {file.name.length > 15
                          ? file.name.substring(0, 12) + '...'
                          : file.name}
                      </span>
                    </div>
                  ) : (
                    <img src={file.url} alt={file.name} />
                  )}
                  <FileOverlay isActive={activeFileId === file.id}>
                    <ViewFileButton
                      type="button"
                      onClick={(e) => openPreview(file, e)}
                    >
                      <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                      View
                    </ViewFileButton>
                    <RemoveFileButton
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(index);
                        setActiveFileId(null);
                      }}
                    >
                      <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                      Remove
                    </RemoveFileButton>
                  </FileOverlay>
                </FileItem>
              ))}

              {maxFiles > 1 && attachments.length < maxFiles && (
                <AddMoreArea onClick={triggerUpload}>
                  <svg
                    width="32"
                    height="32"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                </AddMoreArea>
              )}
            </FileGrid>
          </AttachmentContent>
        )}
      </AttachmentSection>

      <input {...getInputProps()} />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.pdf,.svg"
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        disabled={disabled}
      />

      <Toaster
        isOpen={toasterOpen}
        onClose={() => setToasterOpen(false)}
        title={toasterConfig.title}
        message={toasterConfig.message}
        type={toasterConfig.type}
        duration={5000}
      />

      {previewFile && (
        <ImagePreviewOverlay onClick={closePreview}>
          <ImagePreviewContainer onClick={(e) => e.stopPropagation()}>
            <ImagePreviewClose onClick={closePreview} type="button">
              <svg
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </ImagePreviewClose>
            {previewFile.isPDF ? (
              <ImagePreviewPDF>
                <svg width="64" height="64" fill="#FA4616" viewBox="0 0 16 16">
                  <path d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2zM9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5v2z" />
                  <path d="M4.603 14.087a.81.81 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.68 7.68 0 0 1 1.482-.645 19.697 19.697 0 0 0 1.062-2.227 7.269 7.269 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 0 1 .477.365c.088.164.12.356.127.538.007.188-.012.396-.047.614-.084.51-.27 1.134-.52 1.794a10.954 10.954 0 0 0 .98 1.686 5.753 5.753 0 0 1 1.334.05c.364.066.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 0 1-.354.416.856.856 0 0 1-.51.138c-.331-.014-.654-.196-.933-.417a5.712 5.712 0 0 1-.911-.95 11.651 11.651 0 0 0-1.997.406 11.307 11.307 0 0 1-1.02 1.51c-.292.35-.609.656-.927.787a.793.793 0 0 1-.58.029zm1.379-1.901c-.166.076-.32.156-.459.238-.328.194-.541.383-.647.547-.094.145-.096.25-.04.361.01.022.02.036.026.044a.266.266 0 0 0 .035-.012c.137-.056.355-.235.635-.572a8.18 8.18 0 0 0 .45-.606zm1.64-1.33a12.71 12.71 0 0 1 1.01-.193 11.744 11.744 0 0 1-.51-.858 20.801 20.801 0 0 1-.5 1.05zm2.446.45c.15.163.296.3.435.41.24.19.407.253.498.256a.107.107 0 0 0 .07-.015.307.307 0 0 0 .094-.125.436.436 0 0 0 .059-.2.095.095 0 0 0-.026-.063c-.052-.062-.2-.152-.518-.209a3.876 3.876 0 0 0-.612-.053zM8.078 7.8a6.7 6.7 0 0 0 .2-.828c.031-.188.043-.343.038-.465a.613.613 0 0 0-.032-.198.517.517 0 0 0-.145.04c-.087.035-.158.106-.196.283-.04.192-.03.469.046.822.024.111.054.227.09.346z" />
                </svg>
                <ImagePreviewFileName>{previewFile.name}</ImagePreviewFileName>
                <ImagePreviewPDFLink
                  href={previewFile.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  Open PDF
                </ImagePreviewPDFLink>
              </ImagePreviewPDF>
            ) : (
              <>
                <ImagePreviewImage
                  src={previewFile.url}
                  alt={previewFile.name}
                />
                <ImagePreviewFileName>{previewFile.name}</ImagePreviewFileName>
              </>
            )}
          </ImagePreviewContainer>
        </ImagePreviewOverlay>
      )}
    </>
  );
};

AttachmentUpload.propTypes = {
  attachments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      size: PropTypes.number.isRequired,
      sizeFormatted: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      isImage: PropTypes.bool,
      isPDF: PropTypes.bool,
      isSVG: PropTypes.bool,
    })
  ).isRequired,
  onAttachmentsChange: PropTypes.func.isRequired,
  maxFiles: PropTypes.number,
  maxFileSize: PropTypes.number,
  channel: PropTypes.oneOf(['SMS', 'Push']),
  disabled: PropTypes.bool,
  onError: PropTypes.func,
  defaultExpanded: PropTypes.bool,
};

export default AttachmentUpload;
